import Router from "next/router";

import { getLoginToken, removeLoginTokenAndShopId } from "./login.core";
import { logReqError } from "./log.core";
import { getShopId } from "./shop.core";

function getHeader(option: HeaderOption, additional?: { [key: string]: string }) {
  var headers = {
    ...additional,
  };

  if (option.useToken && getLoginToken()) {
    headers["x-access-token"] = getLoginToken()!;
  }

  if (option.useShopId && getShopId()) {
    headers["shop-id"] = getShopId()!;
  }

  if (option.retailShopId) {
    headers["retailshop-id"] = option.retailShopId;
  }

  const locale = getHeaderLocale(option);
  headers["locale"] = locale;

  return headers;
}

function getHeaderLocale(option: HeaderOption) {
  if (option.locale) {
    return option.locale;
  } else if (Router.locale) {
    return Router.locale;
  } else {
    return "en";
  }
}

interface HeaderOption {
  useToken?: boolean;
  useShopId?: boolean;
  retailShopId?: string;
  locale?: string;
  query?: { [key: string]: string };
}

export async function get<T>(url: string, option: HeaderOption = { useToken: true, useShopId: true, retailShopId: "" }) {
  const headers = { ...getHeader(option) };

  if (option.retailShopId) {
    headers["retailshop-id"] = option.retailShopId;
  }

  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;
  const init = {
    headers,
  };

  let res: Response;

  try {
    res = await fetch(input, init);
  } catch (err) {
    logReqError({ req: { input, init }, resData: err });
    throw err;
  }

  if (res.status === 404) {
    throw new Error("dataNotFound");
  }

  if (res.status !== 200) {
    const errorContent = await res.json();

    if (
      res.status === 401 &&
      errorContent.message &&
      (errorContent.message === "invalidToken" ||
        errorContent.message === "tokenNotExists" ||
        errorContent.message === "invalidUser")
    ) {
      removeLoginTokenAndShopId();
    } else {
      if (res.status >= 300) {
        logReqError({
          req: {
            input,
            init,
          },
          res,
          resData: errorContent,
        });
      }
    }

    throw new Error(`Fetch Failed with url '${url}': ${JSON.stringify(errorContent)}`);
  }

  return {
    status: res.status,
    data: (await res.json()) as T,
  };
}

export async function getDontHandleError<T>(
  url: string,
  option: HeaderOption = { useToken: true, useShopId: true, retailShopId: "" }
) {
  const headers = { ...getHeader(option) };

  if (option.retailShopId) {
    headers["retailshop-id"] = option.retailShopId;
  }

  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;
  const init = {
    headers,
  };

  let res: Response;

  try {
    res = await fetch(input, init);
  } catch (err) {
    logReqError({ req: { input, init }, resData: err });
    throw err;
  }

  const content = await res.json();

  if (res.status === 401 && content.message && (content.message === "invalidToken" || content.message === "tokenNotExists")) {
    removeLoginTokenAndShopId();
  }

  return {
    status: res.status,
    data: content as T,
  };
}

export async function post<T>(
  url: string,
  data: any,
  option: HeaderOption = { useToken: true, useShopId: true, retailShopId: "" }
) {
  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;
  const init = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(option.useToken && { "x-access-token": getLoginToken() }),
      ...(option.useShopId && { "shop-id": getShopId() }),
      ...(option.retailShopId && { "retailshop-id": option.retailShopId }),
      locale: getHeaderLocale(option),
    },
    body: JSON.stringify(data),
  };

  let res: Response;

  try {
    res = await fetch(input, init);
  } catch (err) {
    logReqError({ req: { input, init }, resData: err });
    throw err;
  }

  const resData = await res.json();

  if (res.status === 401 && resData.message && (resData.message === "invalidToken" || resData.message === "tokenNotExists")) {
    removeLoginTokenAndShopId();

    console.error(resData);
    throw new Error(`Fetch Failed with url '${url}': ${JSON.stringify(resData)}`);
  }

  if (res.status >= 300) {
    logReqError({
      req: {
        input,
        init,
      },
      res,
      resData: data,
    });

    console.error(resData);
    throw new Error(`Fetch Failed with url '${url}': ${JSON.stringify(resData)}`);
  }

  return {
    status: res.status,
    data: resData as T,
  };
}

export async function postDontHandleError<T>(
  url: string,
  data: any,
  option: HeaderOption = { useToken: true, useShopId: true, retailShopId: "" }
) {
  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;
  const init = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(option.useToken && { "x-access-token": getLoginToken() }),
      ...(option.useShopId && { "shop-id": getShopId() }),
      ...(option.retailShopId && { "retailshop-id": option.retailShopId }),
      locale: getHeaderLocale(option),
    },
    body: JSON.stringify(data),
  };

  let res: Response;

  try {
    res = await fetch(input, init);
  } catch (err) {
    logReqError({ req: { input, init }, resData: err });
    throw err;
  }

  const resData = res.headers.get("content-length") === "0" ? undefined : ((await res.json()) as T);

  if (res.status >= 500) {
    logReqError({
      req: {
        input,
        init,
      },
      res,
      resData,
    });
  }

  return {
    status: res.status,
    data: resData,
  };
}

export async function postFormData<T>(
  url: string,
  data: FormData,
  option: HeaderOption = { useToken: true, useShopId: true, retailShopId: "" }
) {
  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;
  const init = {
    method: "POST",
    headers: {
      ...(option.useToken && { "x-access-token": getLoginToken() }),
      ...(option.useShopId && { "shop-id": getShopId() }),
      ...(option.retailShopId && { "retailshop-id": option.retailShopId }),
    },
    body: data,
  };

  let res: Response;

  try {
    res = await fetch(input, init);
  } catch (err) {
    logReqError({ req: { input, init }, resData: err });
    throw err;
  }

  if (res.status !== 200) {
    const data = await res.json();

    if (res.status >= 300) {
      logReqError({
        req: {
          input,
          init,
        },
        res,
        resData: data,
      });
    }

    console.error(data);
    throw new Error("Fetch Failed:" + JSON.stringify(data));
  }

  return {
    status: res.status,
    data: (await res.json()) as T,
  };
}

export async function getBlob(url: string, option: HeaderOption = { useToken: true, useShopId: true, retailShopId: "" }) {
  const headers = { ...getHeader(option) };

  const response = await fetch(process.env.NEXT_PUBLIC_URL_ROOT + url, {
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
    throw new Error(`Failed to download PDF: ${errorData.message || response.statusText}`);
  }

  return await response.blob();
}
